// Create-table modal. Phase 1 fields: name, category (required), summary, hot box.
// Subcategories not seeded; users can pick "no subcategory" via the default dropdown
// option, or pick one when seeded later.

import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';
import type { CreateTableRequest } from '@command-floor/shared-types';

interface HierarchyResponse {
  entities: {
    id: string;
    name: string;
    displayOrder: number;
    categories: {
      id: string;
      name: string;
      displayOrder: number;
      subcategories: {
        id: string;
        name: string;
        displayOrder: number;
      }[];
    }[];
  }[];
}

interface Props {
  onClose: () => void;
}

export function CreateTableButton({ onClose }: Props) {
  const [name, setName] = useState('');
  const [entityId, setEntityId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [summary, setSummary] = useState('');
  const [hotBox, setHotBox] = useState('');
  const [error, setError] = useState<string | null>(null);

  const hierarchy = useQuery({
    queryKey: ['hierarchy'],
    queryFn: () => api.get<HierarchyResponse>('/api/admin/hierarchy'),
  });

  // Default to first entity (and its first category) once data loads
  useEffect(() => {
    if (!entityId && hierarchy.data?.entities[0]) {
      setEntityId(hierarchy.data.entities[0].id);
      setCategoryId(hierarchy.data.entities[0].categories[0]?.id ?? '');
    }
  }, [hierarchy.data, entityId]);

  // Categories for the selected entity
  const entityCategories =
    hierarchy.data?.entities.find((e) => e.id === entityId)?.categories ?? [];

  // Subcategories for the selected category
  const subcategories = entityCategories.find((c) => c.id === categoryId)?.subcategories ?? [];

  const createMutation = useMutation({
    mutationFn: async (body: CreateTableRequest) => {
      return api.post<{ id: string }>('/api/tables', body);
    },
    onSuccess: () => {
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Could not create table. Try again.');
      }
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!entityId) {
      setError('Entity is required');
      return;
    }
    if (!categoryId) {
      setError('Category is required');
      return;
    }
    createMutation.mutate({
      name: name.trim(),
      category_id: categoryId,
      subcategory_id: subcategoryId || null,
      summary: summary.trim() || undefined,
      hot_box: hotBox.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-xl rounded-lg border border-aurora bg-surface-panel p-6 shadow-2xl"
      >
        <h2 className="mb-5 text-lg font-semibold">New work effort</h2>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm text-slate-300">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Las Vegas Mini Grand Prix — Due Diligence"
            className="w-full rounded-md border border-aurora-strong bg-surface px-3 py-2 text-sm text-slate-100 focus:border-flag-blue focus:outline-none"
            autoFocus
            required
          />
        </label>

        <div className="mb-4 grid grid-cols-2 gap-4">
          <label>
            <span className="mb-1 block text-sm text-slate-300">Entity</span>
            <select
              value={entityId}
              onChange={(e) => {
                setEntityId(e.target.value);
                const firstCat =
                  hierarchy.data?.entities.find((en) => en.id === e.target.value)?.categories[0];
                setCategoryId(firstCat?.id ?? '');
                setSubcategoryId('');
              }}
              className="w-full rounded-md border border-aurora-strong bg-surface px-3 py-2 text-sm text-slate-100 focus:border-flag-blue focus:outline-none"
              required
            >
              <option value="" disabled>
                Pick one…
              </option>
              {hierarchy.data?.entities.map((entity) => (
                <option key={entity.id} value={entity.id}>
                  {entity.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="mb-1 block text-sm text-slate-300">Category</span>
            <select
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSubcategoryId('');
              }}
              disabled={entityCategories.length === 0}
              className="w-full rounded-md border border-aurora-strong bg-surface px-3 py-2 text-sm text-slate-100 focus:border-flag-blue focus:outline-none disabled:opacity-50"
              required
            >
              <option value="" disabled>
                Pick one…
              </option>
              {entityCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm text-slate-300">Subcategory (optional)</span>
          <select
            value={subcategoryId}
            onChange={(e) => setSubcategoryId(e.target.value)}
            disabled={subcategories.length === 0}
            className="w-full rounded-md border border-aurora-strong bg-surface px-3 py-2 text-sm text-slate-100 focus:border-flag-blue focus:outline-none disabled:opacity-50"
          >
            <option value="">— none —</option>
            {subcategories.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm text-slate-300">Summary (optional)</span>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            placeholder="Project purpose, objectives, deliverables — slow-changing context"
            className="w-full rounded-md border border-aurora-strong bg-surface px-3 py-2 text-sm text-slate-100 focus:border-flag-blue focus:outline-none"
          />
        </label>

        <label className="mb-6 block">
          <span className="mb-1 block text-sm text-slate-300">Hot box (optional)</span>
          <textarea
            value={hotBox}
            onChange={(e) => setHotBox(e.target.value)}
            rows={2}
            placeholder="What's most pressing right now"
            className="w-full rounded-md border border-aurora-strong bg-surface px-3 py-2 text-sm text-slate-100 focus:border-flag-blue focus:outline-none"
          />
        </label>

        {error && <p className="mb-4 text-sm text-flag-red">{error}</p>}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-aurora-strong px-4 py-2 text-sm text-slate-200 hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="rounded-md bg-flag-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating…' : 'Create table'}
          </button>
        </div>
      </form>
    </div>
  );
}
